import {useEffect, useRef, useState} from "react";
import * as v from "valibot";
import {useNextForm} from "@cdx/common/forms-next/FormNext";
import {TextField} from "@cdx/common/forms-next/fields";
import {Col, DSButton, DSFieldSetClassName, Row, Text} from "@cdx/ds";
import useMutation from "../../lib/hooks/useMutation";
import {refusalReason} from "../../lib/mutation-refusal";
import {TotpCodeForm} from "./TotpCodeForm";
import {describeSecurityError, getPasskeyAssertion} from "./webauthn-browser";

/** Where a login that spent a recovery code lands: the Security tab, which then shows a callout. */
export const securityTabAfterRecoveryCode = {
  pathname: "/",
  search: "?show=userProfile.security",
  state: {modalState: {usedRecoveryCode: true}},
};

export type SecondFactorResult = {usedRecoveryCode: boolean};

export const recoveryCodeUsedMessage =
  "You logged in with a recovery code. That code cannot be used again.";

type PasskeyPromptProps = {onDone: () => void; onChooseAnother: (() => void) | null};

const PasskeyPrompt = ({onDone, onChooseAnother}: PasskeyPromptProps) => {
  const [doBegin] = useMutation("secondFactor", "beginPasskey");
  const [doVerify] = useMutation("secondFactor", "verifyPasskey");
  const [pending, setPending] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const runCeremony = async () => {
    setPending(true);
    setError(null);
    try {
      const credential = await getPasskeyAssertion(await doBegin({}));
      await doVerify({credential});
      onDone();
    } catch (e) {
      setError(describeSecurityError(e));
      setPending(false);
    }
  };

  // a browser may refuse a ceremony no click started; "Try again" is that click
  const runCeremonyRef = useRef(runCeremony);
  runCeremonyRef.current = runCeremony;
  useEffect(() => {
    void runCeremonyRef.current();
  }, []);

  if (pending) {
    return (
      <Text type="prose14" color="secondary">
        Waiting for your security key...
      </Text>
    );
  }
  return (
    <Col sp="12px">
      {error && (
        <Text type="prose14" color="error">
          {error}
        </Text>
      )}
      <Row sp="12px" wrap>
        <DSButton variant="primary" onClick={runCeremony}>
          Try again
        </DSButton>
        {onChooseAnother && (
          <DSButton variant="secondary" onClick={onChooseAnother}>
            Choose another method
          </DSButton>
        )}
      </Row>
    </Col>
  );
};

const recoveryCodeSchema = v.object({code: v.pipe(v.string(), v.nonEmpty())});

const RecoveryCodeForm = ({onDone}: {onDone: () => void}) => {
  const [doVerify] = useMutation("secondFactor", "verifyRecoveryCode");
  const {Form, field} = useNextForm({
    initial: {code: ""},
    schema: recoveryCodeSchema,
    onSubmit: ({code}) =>
      doVerify({code}).then(onDone, (e) =>
        Promise.reject(
          refusalReason(e) === "invalid_recovery_code"
            ? {code: "This code is not valid or has been used already."}
            : describeSecurityError(e)
        )
      ),
  });
  return (
    <Form buttonLabel="Log in" className={DSFieldSetClassName}>
      <TextField
        field={field("code")}
        label="Recovery code"
        placeholder="XXXXX-XXXXX"
        autoComplete="one-time-code"
        autoFocus
      />
    </Form>
  );
};

const TotpForm = ({onDone}: {onDone: () => void}) => {
  const [doVerify] = useMutation("secondFactor", "verifyTotp");
  return <TotpCodeForm verify={(code) => doVerify({code})} onDone={onDone} buttonLabel="Log in" />;
};

type Factor = "passkey" | "totp";
type View = "choose" | Factor | "recoveryCode";

const getInitialView = (secondFactors: Factor[]): View => {
  const hasTotp = secondFactors.includes("totp");
  if (hasTotp && secondFactors.includes("passkey")) return "choose";
  return hasTotp ? "totp" : "passkey";
};

const Chooser = ({onPick}: {onPick: (factor: Factor) => void}) => (
  <Col sp="8px">
    <DSButton variant="secondary" onClick={() => onPick("passkey")}>
      Use your security key
    </DSButton>
    <DSButton variant="secondary" onClick={() => onPick("totp")}>
      Enter code from authenticator app
    </DSButton>
  </Col>
);

const getIntro = (view: View, emailHint: string) => {
  switch (view) {
    case "choose":
      return `choose how to confirm it's you to log in as ${emailHint}.`;
    case "passkey":
      return `confirm it's you with your security key to log in as ${emailHint}.`;
    case "totp":
      return `enter the code from your authenticator app to log in as ${emailHint}.`;
    case "recoveryCode":
      return `enter one of your saved recovery codes to log in as ${emailHint}.`;
  }
};

type SecondFactorStepProps = {
  name: string | null;
  emailHint: string;
  secondFactors: Factor[];
  onDone: (result: SecondFactorResult) => void;
  onCancel: () => void;
};

/**
 * Shown after the first factor passed for a user with a security key or an authenticator app. With
 * both, it opens on a chooser; with one, straight on it.
 */
export const SecondFactorStep = ({
  name,
  emailHint,
  secondFactors,
  onDone,
  onCancel,
}: SecondFactorStepProps) => {
  const [view, setView] = useState(() => getInitialView(secondFactors));
  const [doCancel] = useMutation("secondFactor", "cancel");
  const initialView = getInitialView(secondFactors);
  const chooseAnother = initialView === "choose" ? () => setView("choose") : null;

  // a timed-out session rejects the cancel; going back is right either way
  const handleCancel = () => doCancel({}).then(onCancel, onCancel);
  const done = () => onDone({usedRecoveryCode: false});

  const body = () => {
    switch (view) {
      case "choose":
        return <Chooser onPick={setView} />;
      case "passkey":
        return <PasskeyPrompt onDone={done} onChooseAnother={chooseAnother} />;
      case "totp":
        return <TotpForm onDone={done} />;
      case "recoveryCode":
        return <RecoveryCodeForm onDone={() => onDone({usedRecoveryCode: true})} />;
    }
  };

  // the passkey view offers "Choose another method" once its ceremony failed, next to "Try again"
  const renderSwitch = () => {
    if (view === "recoveryCode") {
      if (chooseAnother) {
        return (
          <DSButton variant="tertiary" size="sm" onClick={chooseAnother}>
            Choose another method
          </DSButton>
        );
      }
      return (
        <DSButton variant="tertiary" size="sm" onClick={() => setView(initialView)}>
          {initialView === "totp" ? "Use your authenticator app" : "Use your security key"}
        </DSButton>
      );
    }
    return (
      <>
        {view === "totp" && chooseAnother && (
          <DSButton variant="tertiary" size="sm" onClick={chooseAnother}>
            Choose another method
          </DSButton>
        )}
        <DSButton variant="tertiary" size="sm" onClick={() => setView("recoveryCode")}>
          Use a recovery code
        </DSButton>
      </>
    );
  };

  return (
    <Col sp="24px">
      <Col sp="4px">
        <Text type="label16" color="primary">
          {name ? `Hi ${name},` : "Hi there,"}
        </Text>
        <Text type="prose14" color="secondary">
          {getIntro(view, emailHint)}
        </Text>
      </Col>
      {body()}
      <Row sp="16px" justify="center" wrap>
        {renderSwitch()}
        <DSButton variant="tertiary" size="sm" onClick={handleCancel}>
          Back to login
        </DSButton>
      </Row>
    </Col>
  );
};
